// The arithmetic behind the Insights cards, kept out of the components so it can be tested
// on its own and so every page shows a figure the same way.
//
// The "trend" columns are ported from CGS2022 analysisTopCard.vue and analysisOverviewCard.vue.
// A row's trend figure is the same row found by name in the trend window's data, and is 0
// when that row isn't there.

// ── formatting ──────────────────────────────────────────────────────────────────────────
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const int0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

export function toNum(v) {
  if (v === null || v === undefined || v === '') return NaN
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,%\s]/g, ''))
  return Number.isFinite(n) ? n : NaN
}

// CGS shows "-" when a figure won't parse. Kept.
export function fmtCurrency(v) { const n = toNum(v); return Number.isNaN(n) ? '-' : usd0.format(n) }
export function fmtCurrencyExact(v) { const n = toNum(v); return Number.isNaN(n) ? '-' : usd2.format(n) }
export function fmtInt(v) { const n = toNum(v); return Number.isNaN(n) ? '-' : int0.format(n) }
export function fmtDec(v, places = 2) { const n = toNum(v); return Number.isNaN(n) ? '-' : n.toFixed(places) }
export function fmtPct(v, places = 1) { const n = toNum(v); return Number.isNaN(n) ? '-' : `${n.toFixed(places)}%` }

// $170.2M / $337.7K / $950. The compact form is for headline tiles only. Tables keep whole
// dollars so they can be checked against CGS line by line.
export function fmtCurrencyCompact(v) {
  const n = toNum(v)
  if (Number.isNaN(n)) return '-'
  const a = Math.abs(n)
  if (a >= 1e9) return `${n < 0 ? '-' : ''}$${(a / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${n < 0 ? '-' : ''}$${(a / 1e6).toFixed(1)}M`
  if (a >= 1e3) return `${n < 0 ? '-' : ''}$${(a / 1e3).toFixed(1)}K`
  return usd0.format(n)
}

export function formatAs(v, format) {
  switch (format) {
    case 'cur': return fmtCurrency(v)
    case 'cur2': return fmtCurrencyExact(v)
    case 'curc': return fmtCurrencyCompact(v)
    case 'dec': return fmtDec(v)
    case 'dec3': return fmtDec(v, 3)
    case 'pct': return fmtPct(v)
    case 'pct2': return fmtPct(v, 2)
    case 'int': return fmtInt(v)
    default: return v === null || v === undefined || v === '' ? '-' : String(v)
  }
}

// ── trend ───────────────────────────────────────────────────────────────────────────────
// CGS draws an up, down or flat arrow from a straight comparison of current and trend. Here
// the arrow comes from the same comparison and the change is shown beside it. `lowerIsBetter`
// flips only the colour (as with days on line, where CGS flips the arrows), never the arrow.
export function trendOf(cur, prev, { lowerIsBetter = false } = {}) {
  const c = toNum(cur)
  const p = toNum(prev)
  if (Number.isNaN(c) || Number.isNaN(p)) return { dir: 'none', pct: null, tone: 'flat', prev: p }
  const dir = c > p ? 'up' : c < p ? 'down' : 'flat'
  const pct = p === 0 ? null : ((c - p) / Math.abs(p)) * 100
  let tone = 'flat'
  if (dir !== 'flat') tone = (dir === 'up') !== lowerIsBetter ? 'good' : 'bad'
  return { dir, pct, tone, prev: p }
}

// ── top-N lists ─────────────────────────────────────────────────────────────────────────
// analysisTopCard.processTopFive(): the first `limit` rows as the API sorted them (it never
// re-sorts), each with its trend count and volume found by displayProp. The share column is
// count / totalUnits, as getBusinessPercentage() computes it.
export function topWithTrend(rows, trendRows, displayProp, { limit = 5, totalUnits } = {}) {
  const list = Array.isArray(rows) ? rows.slice(0, limit) : []
  const trend = Array.isArray(trendRows) ? trendRows : []
  const total = toNum(totalUnits)
  return list.map(item => {
    const t = trend.find(x => x[displayProp] === item[displayProp])
    return {
      ...item,
      name: item[displayProp],
      trendCount: t ? t.count : 0,
      trendVolume: t ? t.volume : 0,
      // CGS read `averagedayinline`, which the API never sends (it sends averagedayonline).
      trendAverageDayInLine: t ? (t.averagedayonline ?? t.averagedayinline) : undefined,
      share: Number.isNaN(total) || total === 0 ? null : (toNum(item.count) / total) * 100
    }
  })
}

// Bar widths for a list, relative to its largest value.
export function withBars(rows, prop = 'count') {
  const max = Math.max(0, ...rows.map(r => toNum(r[prop]) || 0))
  return rows.map(r => ({ ...r, barPct: max ? Math.max(2, ((toNum(r[prop]) || 0) / max) * 100) : 0 }))
}

// ── mix (the CGS pie charts) ────────────────────────────────────────────────────────────
// CGS draws pies from [{ title, count }]. They are drawn here as one stacked bar, with each
// slice's share of the sum. CGS shows nothing when every count is 0, and so does this.
export function mixOf(points, { labelProp = 'title', valueProp = 'count' } = {}) {
  const pts = (Array.isArray(points) ? points : []).map(p => ({ label: p[labelProp], value: toNum(p[valueProp]) || 0 }))
  const sum = pts.reduce((s, p) => s + p.value, 0)
  if (!sum) return []
  return pts.filter(p => p.value > 0).map(p => ({ ...p, pct: (p.value / sum) * 100 }))
}

export const MIX_PALETTE = ['#123a71', '#5f79c7', '#e9bd54', '#398b87', '#a3a4b5', '#b4462f', '#7d5ba6', '#1d7a4f', '#c97a2b', '#4b5563']

// ── overview statistics ─────────────────────────────────────────────────────────────────
// analysisOverviewCard.assignInsightsData(): each row is insightStatistics found by `title`,
// beside the same title in insightTrendStatistics.
export function statRows(defs, stats, trendStats) {
  const s = Array.isArray(stats) ? stats : []
  const t = Array.isArray(trendStats) ? trendStats : []
  return defs.map(d => ({
    label: d.display,
    value: d.value,
    cur: s.find(x => x.title === d.value) || null,
    prev: t.find(x => x.title === d.value) || null
  }))
}
